import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { isCurrencyCode, type CurrencyCode } from '@ecobox/domain';
import {
  CommandValidationError,
  NoNumbersLeftError,
  PICKUP_STEP_LABELS,
  buildArrivePayload,
  buildCompletePickupPayload,
  computeInvoice,
  dialCodeFor,
  initialPickupForm,
  pickupSubject,
  numbersNeeded,
  pickupContext,
  pickupProblems,
  pickupStepProblems,
  pickupStepsFor,
  type CommandProblem,
  type InvoiceView,
  type OpsPickupRequest,
  type OpsRun,
  type PickupForm,
  type PickupStepId,
  type PickupSubject,
  type StopView,
} from '@ecobox/firebase';
import { Badge, Button, Callout, Card, UpfrontNumber, useToast } from '@ecobox/ui';
import { PhotoField } from '../../components/PhotoField';
import { useDriverData } from '../../hooks/useDriver';
import { useGeo } from '../../hooks/useGeo';
import { useTrackingChoice } from '../../hooks/useUpfrontNumber';
import { usePermissions } from '../../sessions';
import { dataUrlToBlob } from '../../session/files';
import { useServices } from '../../services';
import { SignaturePad } from '@ecobox/ui';
import { FailForm } from './FailForm';
import { BackLink, Problems, Progress, fmt, stopStatus } from './shared';
import {
  BoxesStep,
  ConsigneeStep,
  ContentsStep,
  DropStep,
  PayStep,
  VisitStep,
  type StepProps,
} from './pickupSteps';

/** One pickup stop (05 §5.2): customer, empty-box drop and cargo intake, step by step. Works with no signal. */
export function PickupStop(props: {
  run: OpsRun & { waiting: boolean };
  stop: StopView;
  request: OpsPickupRequest | undefined;
  problems: CommandProblem[];
}) {
  // The stop carries the contact and the wants; the request, when it arrives, adds the customer on
  // file. If it arrives after the screen opened, start again from it.
  const subject = pickupSubject(props.request, props.stop);
  return <PickupFlow key={subject.requestId ?? 'stop-only'} {...props} subject={subject} />;
}

function PickupFlow({
  run,
  stop,
  subject,
  problems,
}: {
  run: OpsRun & { waiting: boolean };
  stop: StopView;
  subject: PickupSubject;
  problems: CommandProblem[];
}) {
  const data = useDriverData();
  const cfg = data.config;
  const { writer, uploads, leases } = useServices();
  const toast = useToast();
  const { fix } = useGeo(true);

  const [form, setForm] = useState<PickupForm>(() => initialPickupForm(subject));
  const [step, setStep] = useState<PickupStepId>('visit');
  const [mode, setMode] = useState<'pickup' | 'fail'>('pickup');
  const [photos, setPhotos] = useState<File[]>([]);
  const [signature, setSignature] = useState<string | null>(null);
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ trackingNo?: string; receiptNo?: string } | null>(null);

  const status = stopStatus(stop.status, stop.waiting);
  const active = run.status === 'active';
  const finished = stop.status === 'done' || stop.status === 'failed' || stop.status === 'skipped';
  // The tracking number of the cargo taken here, shown from the start of the Boxes step so the
  // driver can write it on each box (05 §5.2). Reading it uses no number.
  // It follows the customer's own numbering series (03 §3.5), and a driver allowed to may type the
  // number of a pre-printed airway bill instead (D-27).
  const upfront = useTrackingChoice(
    form.takeCargo && !finished ? run.branchId : null,
    form.cargo.shipper.customer?.id,
  );
  const canType = usePermissions().has('cargo.tracking.custom');
  const generatedNumber = upfront.state.status === 'ready' ? upfront.state.number : null;
  const typed =
    canType && upfront.custom && upfront.custom.value !== generatedNumber ? upfront.custom : null;
  const shownNumber = typed ? typed.value : generatedNumber;
  const numberChanged =
    !typed && upfront.state.status === 'ready' && upfront.state.changedFrom !== undefined;

  // ---- the price: the counter's own function over the cached rate cards ----
  const branch = cfg.branches.get(run.branchId);
  const sector = branch ? cfg.sectors.get(branch.sectorId) : undefined;
  const currency: CurrencyCode = isCurrencyCode(sector?.currency)
    ? sector.currency
    : isCurrencyCode(run.currency)
      ? run.currency
      : 'AED';
  const city = form.cargo.consignee.cityId
    ? cfg.cityMap.get(form.cargo.consignee.cityId)
    : undefined;
  const service = form.cargo.service ?? 'sea';
  const cards = useMemo(
    () =>
      cfg.cardsFor({
        branchId: run.branchId,
        parentHubId: branch?.parentHubId,
        sectorId: sector?.code,
        service,
        today: data.today,
      }),
    [cfg, run.branchId, branch?.parentHubId, sector?.code, service, data.today],
  );
  const addOns = useMemo(() => cfg.addOnsFor(cards), [cfg, cards]);
  const addOnCodes = useMemo(() => {
    const offered = new Set(addOns.map((a) => a.code));
    return form.cargo.addOnCodes.filter((c) => offered.has(c));
  }, [addOns, form.cargo.addOnCodes]);
  const invoice: InvoiceView = useMemo(
    () =>
      form.takeCargo
        ? computeInvoice({
            cards,
            city,
            servingBranchId: city ? cfg.coverage.get(city.id)?.servingBranchId : undefined,
            boxes: form.cargo.boxes,
            sizes: cfg.sizeMap,
            declaredValueMinor: form.cargo.declaredMinor,
            addOnCodes,
            vatRatePercent: sector?.vatRatePercent ?? 0,
            currency,
            emptyBoxPrices: cfg.emptyPrices(run.branchId),
            promotions: cfg.promotionsFor(data.today),
            ...(sector
              ? {
                  sale: {
                    date: data.today,
                    originSectorId: sector.code,
                    originBranchId: run.branchId,
                    service,
                  },
                }
              : {}),
          })
        : { ok: false, reason: 'No cargo at this stop.' },
    [form, cards, city, cfg, addOnCodes, sector, currency, run.branchId, data.today, service],
  );
  const ctx = pickupContext({
    branchId: run.branchId,
    currency,
    dial: dialCodeFor(sector?.code),
    city,
    originSectorId: sector?.code,
    routes: cfg.routes,
    sizes: cfg.sizeMap,
    invoice,
    destinationCountry: city?.country,
  });
  const checked: PickupForm = { ...form, photoCount: photos.length, signed: signature !== null };
  const steps = pickupStepsFor(form);
  const index = Math.max(0, steps.indexOf(step));
  const here = steps[index] ?? 'visit';
  const hereProblems = pickupStepProblems(here, checked, ctx);
  const numberProblem = !form.takeCargo
    ? null
    : typed
      ? typed.problem
        ? `The tracking number you typed: ${typed.problem}`
        : null
      : upfront.state.status === 'ready'
        ? numberChanged
          ? 'The tracking number changed: check the new number on Boxes first.'
          : null
        : upfront.state.status === 'loading'
          ? 'Waiting for the tracking number.'
          : upfront.state.status === 'offline_empty'
            ? 'No tracking number yet: connect to the internet once and it appears on Boxes.'
            : 'No tracking number yet: tap Retry on Boxes.';
  const allProblems = [...pickupProblems(checked, ctx), ...(numberProblem ? [numberProblem] : [])];
  const last = index === steps.length - 1;
  const stepProps: StepProps = { form, setForm, ctx, cfg, currency, invoice };

  const arrive = async () => {
    setBusy(true);
    try {
      const geo = await fix();
      writer.send(
        'arriveStop',
        buildArrivePayload({
          runId: run.id,
          stopId: stop.id,
          at: new Date().toISOString(),
          geo,
          notifyCustomer: notify,
        }),
      );
    } catch (err) {
      toast.show({
        title: 'Not saved',
        message: err instanceof Error ? err.message : String(err),
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (allProblems.length > 0) return;
    setBusy(true);
    try {
      const geo = await Promise.race([
        fix(),
        new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 3000)),
      ]);
      // Numbers come from this phone's leased ranges: they work with no signal (02 §2.2). The
      // tracking number is exactly the one shown on Boxes; if this phone has used it meanwhile (a
      // second pickup open in another tab), the new one is shown and nothing is saved yet.
      const need = numbersNeeded(checked);
      let trackingNo: string | undefined;
      const seriesId = upfront.seriesId;
      const viaSeries = seriesId === run.branchId ? undefined : seriesId;
      if (need.tracking) {
        if (!shownNumber) return;
        if (typed) {
          trackingNo = typed.value;
        } else {
          const current = await leases.peekNumber('tracking', run.branchId, viaSeries);
          if (current !== shownNumber) {
            upfront.watch?.show(current);
            setStep('boxes');
            return;
          }
          const took = await leases.takeShownNumber(
            'tracking',
            run.branchId,
            shownNumber,
            viaSeries,
          );
          if (!took.taken) {
            upfront.watch?.show(took.next);
            setStep('boxes');
            return;
          }
          trackingNo = took.number;
        }
      }
      const receiptNo = need.receipt ? await leases.nextNumber('receipt', run.branchId) : undefined;
      const photoPaths: string[] = [];
      for (const photo of photos)
        photoPaths.push(await uploads.enqueueUpload(photo, { kind: 'pod' }));
      const signaturePath = signature
        ? await uploads.enqueueUpload(dataUrlToBlob(signature), { kind: 'signature' })
        : undefined;
      const toSend: PickupForm = {
        ...form,
        cargo: {
          ...form.cargo,
          addOnCodes,
          seasonTags: cfg.suggestedSeasons(data.today),
        },
      };
      writer.send(
        'completePickup',
        buildCompletePickupPayload({
          runId: run.id,
          stopId: stop.id,
          form: toSend,
          ctx,
          numbers: trackingNo
            ? {
                trackingNo,
                receiptNo,
                ...(typed ? { trackingSource: 'typed' as const } : { seriesId }),
              }
            : undefined,
          expectedGrossMinor: invoice.ok ? invoice.grossMinor : undefined,
          photoPaths,
          signaturePath,
          geo,
        }),
      );
      if (trackingNo && !typed) upfront.watch?.markUsed(trackingNo);
      setSaved({
        ...(trackingNo ? { trackingNo } : {}),
        ...(receiptNo ? { receiptNo } : {}),
      });
      toast.show({
        title: 'Pickup saved',
        message: 'It goes to the server when you have signal.',
        tone: 'success',
      });
    } catch (err) {
      setError(
        err instanceof NoNumbersLeftError
          ? err.message
          : err instanceof CommandValidationError && err.issues.length > 0
            ? err.issues.join('; ')
            : err instanceof Error
              ? err.message
              : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const title = subject.contact?.name ?? stop.label;
  const header = (
    <>
      <BackLink to={`/run/${run.id}`}>The run</BackLink>
      <div>
        <h1 className="ops-title">{title}</h1>
        <p className="ops-muted" style={{ margin: 0 }}>
          Stop {stop.sequence + 1} · pickup <Badge tone={status.tone}>{status.label}</Badge>
        </p>
      </div>
    </>
  );

  if (finished) {
    return (
      <div className="ops-stack">
        {header}
        <Problems problems={problems} stopId={stop.id} />
        <Card title={stop.status === 'done' ? 'Pickup saved' : 'Pickup not done'}>
          <div className="ops-stack">
            {stop.status === 'done' ? (
              <>
                {saved?.trackingNo ? (
                  <p style={{ margin: 0 }}>
                    Tracking number <span className="ops-code">{saved.trackingNo}</span>
                    {saved.receiptNo ? (
                      <>
                        {' '}
                        · receipt <span className="ops-code">{saved.receiptNo}</span>
                      </>
                    ) : null}
                  </p>
                ) : null}
                {(stop.outcome?.collectedMinor ?? 0) > 0 ? (
                  <p style={{ margin: 0 }}>
                    Cash taken: {fmt(stop.outcome?.collectedMinor ?? 0, run.currency)}. Hand it to
                    the cashier when you end the run.
                  </p>
                ) : null}
              </>
            ) : (
              <p style={{ margin: 0 }}>
                {stop.outcome?.reason ? `Reason: ${stop.outcome.reason.replace(/_/g, ' ')}.` : ''}{' '}
                {stop.outcome?.note ?? ''}
              </p>
            )}
            {stop.waiting ? (
              <p className="ops-muted" style={{ margin: 0 }}>
                Saved on this phone. It goes to the server when you have signal.
              </p>
            ) : null}
            <Link to={`/run/${run.id}`}>
              <Button size="touch" variant="cta" block>
                Back to the run
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (mode === 'fail') {
    return (
      <div className="ops-stack">
        {header}
        <FailForm
          runId={run.id}
          stopId={stop.id}
          type="failPickup"
          onCancel={() => setMode('pickup')}
        />
      </div>
    );
  }

  return (
    <div className="ops-stack">
      {header}
      <Problems problems={problems} stopId={stop.id} />
      <div className="ops-stack" data-tour="pickup-steps">
        <div className="ops-step" role="status">
          Step {index + 1} of {steps.length} · {PICKUP_STEP_LABELS[here]}
        </div>
        <Progress percent={Math.round(((index + 1) / steps.length) * 100)} label="Pickup steps" />
      </div>

      {form.takeCargo &&
      (here === 'boxes' || here === 'consignee' || here === 'contents' || here === 'pay') &&
      cfg.loading ? (
        <Callout tone="info">Loading prices and boxes…</Callout>
      ) : null}
      {form.takeCargo && !cfg.enabled ? (
        <Callout tone="warning">
          The prices are not on this phone yet. Connect once to load them.
        </Callout>
      ) : null}

      {here === 'visit' ? (
        <VisitStep
          subject={subject}
          run={run}
          form={form}
          setForm={setForm}
          arrived={stop.status === 'arrived'}
          arriving={busy}
          onArrive={() => void arrive()}
          notify={notify}
          setNotify={setNotify}
          onFail={() => setMode('fail')}
          canAct={active}
        />
      ) : null}
      {here === 'drop' ? <DropStep {...stepProps} /> : null}
      {here === 'boxes' ? (
        <>
          <UpfrontNumber
            state={upfront.state}
            data-tour="pickup-number"
            hint="Write this on each box and tell the customer. It is used when you save the pickup."
            offlineText="Carry on with the pickup. The number appears here as soon as the phone has signal; the pickup can be saved then."
            writeOn="on each box"
            onRetry={() => upfront.watch?.retry()}
            onAccept={() => upfront.watch?.accept()}
            series={{
              options: upfront.options,
              value: upfront.seriesId,
              onChange: upfront.pickSeries,
              why: upfront.why,
            }}
            {...(canType ? { onEdit: () => upfront.setCustom(generatedNumber ?? '') } : {})}
            custom={
              canType && upfront.custom
                ? {
                    value: upfront.custom.value,
                    problem: upfront.custom.problem,
                    onChange: (v) => upfront.setCustom(v),
                    onBack: () => upfront.setCustom(null),
                  }
                : undefined
            }
          />
          <BoxesStep {...stepProps} />
        </>
      ) : null}
      {here === 'consignee' ? <ConsigneeStep {...stepProps} /> : null}
      {here === 'contents' ? <ContentsStep {...stepProps} /> : null}
      {here === 'pay' ? (
        <PayStep {...stepProps} addOns={addOns.map((a) => ({ code: a.code, label: a.label }))} />
      ) : null}
      {here === 'proof' ? (
        <div className="ops-stack">
          <PhotoField label="Photos" files={photos} onChange={setPhotos} max={10} />
          <SignaturePad label="Customer’s signature" value={signature} onChange={setSignature} />
          <Summary form={checked} invoice={invoice} currency={run.currency} />
        </div>
      ) : null}

      {hereProblems.length > 0 ? (
        <ul className="ops-muted" aria-label="Still needed" style={{ margin: 0, paddingLeft: 18 }}>
          {hereProblems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      {last && allProblems.length > 0 && here === 'proof' ? (
        <ul role="alert" className="ops-error" style={{ margin: 0, paddingLeft: 18 }}>
          {allProblems
            .filter((p) => !hereProblems.includes(p))
            .map((p) => (
              <li key={p}>{p}</li>
            ))}
        </ul>
      ) : null}
      {error ? (
        <p role="alert" className="ops-error">
          {error}
        </p>
      ) : null}

      <div className="ops-actionbar" data-tour="stop-actions">
        {last ? (
          <Button
            size="touch"
            variant="cta"
            block
            loading={busy}
            disabled={!active || allProblems.length > 0}
            onClick={() => void submit()}
          >
            Save the pickup
          </Button>
        ) : (
          <Button
            size="touch"
            variant="cta"
            block
            disabled={hereProblems.length > 0}
            onClick={() => {
              const next = steps[index + 1];
              if (next) setStep(next);
            }}
          >
            Next: {PICKUP_STEP_LABELS[steps[index + 1] ?? 'proof']}
          </Button>
        )}
        {index > 0 ? (
          <Button
            size="lg"
            variant="secondary"
            block
            onClick={() => {
              const prev = steps[index - 1];
              if (prev) setStep(prev);
            }}
          >
            Back
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function Summary({
  form,
  invoice,
  currency,
}: {
  form: PickupForm;
  invoice: InvoiceView;
  currency: string;
}) {
  const drops = form.drops.filter((d) => (d.qty ?? 0) > 0);
  return (
    <Card title="Check before you save">
      <div className="ops-stack">
        {drops.length > 0 ? (
          <div>Empty boxes left: {drops.map((d) => `${d.qty} × ${d.sizeId}`).join(', ')}</div>
        ) : null}
        {form.takeCargo ? (
          <>
            <div>
              {form.cargo.boxes.length} box{form.cargo.boxes.length === 1 ? '' : 'es'} to{' '}
              {form.cargo.consignee.name} in {form.cargo.consignee.cityText}
            </div>
            {invoice.ok ? (
              <div>
                Total {fmt(invoice.grossMinor, invoice.currency)}
                {form.cargo.payments.length > 0
                  ? `, cash taken ${fmt(
                      form.cargo.payments.reduce((n, p) => n + (p.amountMinor ?? 0), 0),
                      invoice.currency,
                    )}`
                  : ', unpaid'}
              </div>
            ) : null}
          </>
        ) : (
          <div>No cargo at this stop.</div>
        )}
        {(form.emptyBoxCashMinor ?? 0) > 0 ? (
          <div>Cash for empty boxes: {fmt(form.emptyBoxCashMinor ?? 0, currency)}</div>
        ) : null}
      </div>
    </Card>
  );
}
